import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/properties', label: t('nav.properties') },
    { to: '/about', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 w-full px-6 py-4 lg:px-12 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background-dark/95 backdrop-blur-xl border-b border-white/10 shadow-2xl py-3' 
          : 'bg-transparent border-b border-white/5'
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <NavLink 
          to="/" 
          className="flex items-center gap-3 text-white group"
          onClick={() => {
            if (window.location.pathname === '/') {
              window.location.reload();
            }
          }}
        >
          <motion.div 
            whileHover={{ scale: 1.1, rotate: 5 }}
            className="size-10 flex items-center justify-center bg-primary/20 rounded-xl backdrop-blur-sm border border-primary/30 text-primary group-hover:bg-primary group-hover:text-black transition-all duration-300"
          >
            <span className="material-symbols-outlined text-2xl">apartment</span>
          </motion.div>
          <div>
            <h2 className="text-white text-xl font-bold tracking-tight">Sunset</h2>
            <p className="text-primary text-[10px] font-semibold uppercase tracking-widest -mt-1">Apartments</p>
          </div>
        </NavLink>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <NavLink 
              key={link.to}
              to={link.to}
              className={({ isActive }) => `
                relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300
                ${isActive 
                  ? 'text-primary' 
                  : 'text-white/70 hover:text-white hover:bg-white/5'
                }
              `}
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    <motion.div 
                      layoutId="activeNav"
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        
        {/* Desktop CTA & Language */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <NavLink 
              to="/contact" 
              className="flex items-center justify-center h-11 px-6 rounded-xl bg-primary text-black hover:bg-primary-hover transition-all text-sm font-bold shadow-[0_0_20px_rgba(212,175,53,0.3)] hover:shadow-[0_0_30px_rgba(212,175,53,0.5)]"
            >
              {t('nav.contact')}
            </NavLink>
          </motion.div>
        </div>
        
        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <motion.button 
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="flex items-center justify-center px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-primary/30 transition-all text-white"
          >
            <span className="material-symbols-outlined text-xl">
              {isMobileMenuOpen ? 'close' : 'menu'}
            </span>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mt-4 pb-4 border-t border-white/10 bg-background-dark/95 backdrop-blur-xl rounded-b-xl"
          >
            <nav className="flex flex-col gap-2 pt-4">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <NavLink 
                    to={link.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) => `
                      block px-4 py-3 rounded-lg text-base font-medium transition-all
                      ${isActive 
                        ? 'text-primary bg-primary/10' 
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                      }
                    `}
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-4 px-4"
              >
                <NavLink 
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-center py-3 rounded-xl bg-primary text-black font-bold"
                >
                  {t('nav.contact')}
                </NavLink>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
